import { Form, Input, notification } from 'antd'
import { useFetch } from 'common/hooks'
import { useMergeState } from 'common/hooks/useMergeState'
import { useCallback, useEffect, useRef } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { createGetContactFormSubmissionEndpoint } from 'utils/endpoints'
import methods from 'utils/methods'

import { Button } from '../../elements/buttons/button'

const { Item } = Form

const ERROR_MESSAGE = 'There has been an error, please try again later.'
const SUCCESS_MESSAGE =
  'We have received your inquiry. We will contact you as soon as possible.'

const MAX_PHONE_NUMBER_LENGTH = 12

const PATTERN = /^[\d\s()+-]+$/

const initialState = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: ''
}

const ContactForm = ({ defaultValues, className }) => {
  const [form] = Form.useForm()
  const recaptchaReference = useRef({})
  const { fetchData, isLoading } = useFetch()
  const [formData, setFormData] = useMergeState(initialState)

  useEffect(() => {
    if (defaultValues) form.resetFields()
  }, [defaultValues])

  const handleChange = useCallback(
    event => {
      const { id, value } = event.target
      if (id === 'phone' && !PATTERN.test(value)) event.target.value = value.slice(0, -1)
      else setFormData({ [id]: value })
    },
    [setFormData]
  )

  const handleOnFinish = useCallback(() => form.submit(), [form])

  const handleSubmit = async () => {
    try {
      const token = await recaptchaReference.current.executeAsync()
      const { response, body } = await fetchData(
        createGetContactFormSubmissionEndpoint(),
        {
          method: methods.POST,
          body: JSON.stringify({ data: { ...formData } })
        }
      )
      if (!response.ok || !token) {
        throw new Error(body.error.message)
      }
      notification.success({
        message: 'Success',
        description: SUCCESS_MESSAGE
      })
      form.resetFields()
    } catch {
      notification.error({
        message: 'Error',
        description: ERROR_MESSAGE
      })
    }
  }

  return (
    <div className={`${className}`}>
      <Form
        form={form}
        className="form-fields-container"
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={defaultValues}
        validateTrigger="onBlur"
      >
        <Item
          name="name"
          label="Full Name"
          rules={[
            {
              required: true,
              message: 'Name is mandatory'
            }
          ]}
        >
          <Input
            placeholder="Please input your name"
            size="large"
            value={formData['name']}
            onChange={handleChange}
          />
        </Item>
        <Item
          name="email"
          label="Email"
          rules={[
            {
              required: true,
              message: 'Email is mandatory'
            },
            {
              type: 'email',
              message: 'Please enter a valid email'
            }
          ]}
        >
          <Input
            placeholder="example@youremail.com"
            size="large"
            value={formData['email']}
            onChange={handleChange}
          />
        </Item>
        <Item name="phone" label="Phone Number">
          <Input
            type="text"
            onInput={handleChange}
            value={formData['phone']}
            maxLength={MAX_PHONE_NUMBER_LENGTH}
            placeholder="Enter your phone number"
          />
        </Item>
        <Item
          name="subject"
          label="Subject"
          rules={[
            {
              required: true,
              message: 'Please input a subject!'
            }
          ]}
        >
          <Input
            placeholder="Please input a subject"
            size="large"
            value={formData['subject']}
            onChange={handleChange}
          />
        </Item>
        <Item
          name="message"
          label="Message"
          rules={[
            {
              required: true,
              message: 'Please input a message!'
            }
          ]}
        >
          <Input.TextArea
            style={{ height: 300 }}
            placeholder="What would you like to tell us?"
            size="large"
            value={formData['message']}
            onChange={handleChange}
          />
        </Item>
      </Form>
      <ReCAPTCHA
        ref={recaptchaReference}
        sitekey={process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY}
        size="invisible"
      />
      <div className="form-button-container">
        <Button
          className="form-button"
          text="Send Message"
          disabled={isLoading}
          onClick={handleOnFinish}
        />
      </div>
    </div>
  )
}

export default ContactForm
